import { useMemo } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { ReactComponent as EnvironmentsIcon } from '../../../assets/20x20/environments.svg';
import useCachedEnvironments from '../../../hooks/useCachedEnvironments';
import type { AgentContext } from '../../../types';
import { capitalize } from '../../../utils/common';
import { Flex } from '../../Flex';
import EnvPickerButton from './EnvPickerButton';

const ChipButton = styled(Flex)`
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  color: ${({ theme }) => theme.palette.common.gray4};

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    display: block;
  }

  svg path {
    stroke: ${({ theme }) => theme.palette.common.gray4};
  }

  &:hover {
    background: ${({ theme }) => theme.palette.common.gray10};
  }
`;

type Props = {
  context?: AgentContext;
  onContextChange?: (context: AgentContext | undefined) => void;
};

const EnvDisplayChip = ({ context, onContextChange }: Props) => {
  const { data: environments } = useCachedEnvironments();

  const selectedEnvs = useMemo(() => {
    const envIds = context?.environmentIds ?? [];
    return (environments ?? []).filter((env) => envIds.includes(env.id));
  }, [environments, context?.environmentIds]);

  if (selectedEnvs.length === 0) return null;

  const totalEnvs = environments?.length ?? 0;
  const isAllSelected = totalEnvs > 1 && selectedEnvs.length === totalEnvs;
  const label = isAllSelected
    ? 'All environments'
    : selectedEnvs.length === 1
    ? capitalize(selectedEnvs[0].name)
    : `${capitalize(selectedEnvs[0].name)} + ${selectedEnvs.length - 1} more`;

  return (
    <EnvPickerButton
      context={context}
      onContextChange={onContextChange}
      tooltip={null}
      menuButton={
        <ChipButton>
          <EnvironmentsIcon />
          <Typography sx={{ fontSize: '13px' }}>{label}</Typography>
        </ChipButton>
      }
    />
  );
};

export default EnvDisplayChip;
